import { FC, MouseEvent, ReactNode, useEffect, useState } from 'react';
import {
  TableFilterOption,
  TableFilterOptionItem,
  TableFilterOptions,
  TableFilters,
  TableFiltersChangeCallback,
} from '../filters/types';
import TableFilterContext from './TableFilterContext';

export interface TableFilterContextProviderProps {
  isFiltersLoading?: boolean;
  filterOptions?: TableFilterOptions;
  onFiltersChange?: TableFiltersChangeCallback;
  initialFiltersModel?: TableFilters;
  resultsCount?: number;
  children: ReactNode;
}

const TableFilterContextProvider: FC<TableFilterContextProviderProps> = ({
  isFiltersLoading,
  filterOptions,
  onFiltersChange,
  initialFiltersModel,
  resultsCount,
  children,
}) => {
  const emptyFilters = filterOptions?.reduce<TableFilters>((acc: TableFilters, filter: TableFilterOption) => {
    const currentFilters = { ...acc };
    currentFilters[filter.id] = [];

    return currentFilters as TableFilters;
  }, {} as TableFilters);

  const [selectedFilters, setSelectedFilters] = useState<TableFilters>(emptyFilters || {});
  const [appliedFilters, setAppliedFilters] = useState<TableFilters>(initialFiltersModel || emptyFilters || {});
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [defaultAnchor, setDefaultAnchor] = useState<null | HTMLElement>(null);
  const [open, setOpen] = useState<boolean>(false);

  useEffect(() => {
    setSelectedFilters(appliedFilters);
    onFiltersChange?.(appliedFilters);
  }, [appliedFilters, setSelectedFilters, onFiltersChange]);

  const applyFilters = () => {
    setAppliedFilters(selectedFilters);
    closeFilters();
  };

  const clearFilters = () => {
    setAppliedFilters(emptyFilters!);
  };

  const deleteFilter = (filterId: string, optionId: string) => {
    setAppliedFilters((prev) => {
      const currentFilters = { ...prev };
      currentFilters[filterId] = currentFilters[filterId].filter((option) => option.id !== optionId);

      return currentFilters;
    });
  };

  const changeFilters = (filterId: string, options: TableFilterOptionItem[]) => {
    setSelectedFilters((prev) => {
      const currentFilters = { ...prev };
      currentFilters[filterId] = options;

      return currentFilters;
    });
  };

  const openFilters = (event?: MouseEvent<HTMLElement>) => {
    setOpen(!open);

    if (event) {
      setAnchorEl(anchorEl ? null : event.currentTarget);
      setDefaultAnchor(event.currentTarget);
    } else {
      setAnchorEl(anchorEl ? null : defaultAnchor);
    }
  };

  const closeFilters = () => {
    setOpen(false);
    setAnchorEl(null);
  };

  return (
    <TableFilterContext.Provider
      value={{
        filterOptions,
        selectedFilters,
        appliedFilters,
        resultsCount,
        applyFilters,
        clearFilters,
        deleteFilter,
        changeFilters,
        openFilters,
        closeFilters,
        open,
        anchorEl,
        onFiltersChange,
        isFiltersLoading,
      }}
    >
      {children}
    </TableFilterContext.Provider>
  );
};

export default TableFilterContextProvider;
